import React, { useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import { useParams, Link, useNavigate } from 'react-router';
import { format } from 'date-fns';
import {
  useAlertConfigById,
  useDeleteAlertConfig,
} from './hooks/useAlertConfigs';
import { useCreateAlertRun } from './hooks/useAlertRuns';
import { useAlertCategories } from './hooks/useAlertCategories';
import PageTitle from '../headers/PageTitle';
import { Spinner, Modal, Alert } from 'react-bootstrap';
import { StylishNewButton } from '../../components/DesignSystems/New/StylishNewButton';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from '../../slices/commonSelectors';
import { useDistributionLists } from '../../hooks/useDistributionLists';
import { DGPTAlertConfig, DGPTAlertCategory } from '../../types/dgptAlerts';
import { useAors } from '../../hooks/useAors';
import { AlertConfigSummary } from './AlertConfigSummary';

const _SEVERITY_LABELS: Record<
  number,
  { label: string; variant: 'secondary' | 'info' | 'warning' | 'danger' }
> = {
  1: { label: '1 (Lowest)', variant: 'secondary' },
  2: { label: '2', variant: 'info' },
  3: { label: '3', variant: 'warning' },
  4: { label: '4', variant: 'danger' },
  5: { label: '5 (Highest)', variant: 'danger' },
};

// Helper function to format date/time or return 'Never'
const _formatLastRun = (dateString: string | undefined): string => {
  if (!dateString) return 'Never';
  try {
    return format(new Date(dateString), 'Pp'); // e.g., 09/15/2023, 3:30 PM
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

interface AlertConfigSummaryPageProps {
  alertConfigId: string;
  onEdit?: (config: DGPTAlertConfig) => void;
}

const AlertConfigSummaryPage: React.FC<AlertConfigSummaryPageProps> = ({
  alertConfigId: _alertConfigId,
  onEdit: _onEdit,
}) => {
  const aorText = useAorText();
  const { alert_config_id: urlAlertConfigId } = useParams<{
    alert_config_id: string;
  }>();
  const navigate = useNavigate();
  // Check for warning conditions
  const groupGuid = useSelector(selectGroupGuid);
  const { data: allAors } = useAors();
  const { data: teams } = useDistributionLists(groupGuid);
  const { data: categories } = useAlertCategories();

  // Determine if warnings should be shown
  const hasAors = allAors?.length || 0 > 0;
  const hasTeams = Array.isArray(teams) && teams.length > 0;
  const hasCategoriesWithoutTeams =
    categories?.some(
      (category: DGPTAlertCategory) =>
        !category.teams || category.teams.length === 0
    ) || false;

  // Check if any warning condition is true
  const hasWarningCondition =
    !hasAors || !hasTeams || hasCategoriesWithoutTeams;

  // Use the new hook to fetch the specific config by ID
  const {
    data: config,
    isLoading,
    error,
  } = useAlertConfigById(urlAlertConfigId || '');

  // Instantiate mutation hooks
  const { mutate: runTest, isPending: isTestRunning } = useCreateAlertRun();
  const deleteConfig = useDeleteAlertConfig();

  // State for activate confirmation modal
  const [showActivateModal, setShowActivateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const handleTest = (): void => {
    if (config) {
      runTest({
        dgptAlertConfigId: config.id,
        status: 'Not Started' as const,
        triggerResults: {},
      });
    }
  };

  const handleDelete = (): void => {
    if (config) {
      deleteConfig.mutate(config.id, {
        onSuccess: () => {
          navigate('/aialerts');
        },
      });
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !config) {
    return <div>Error loading alert configuration</div>;
  }

  const formatLastRun = (date: string | undefined) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  return (
    <div className="p-4 pt-0">
      <PageTitle
        title="Alert Configuration Summary"
        parents={['Alert Configurations']}
        onBack={() => navigate('/aialerts')}
      />

      {/* Display warning if any condition is true */}
      {hasWarningCondition && (
        <Alert variant="warning" className="mb-3">
          <strong>Some configuration requirements are not met:</strong>
          <ul className="mt-2 mb-0">
            {!hasAors && (
              <li>
                At least one{' '}
                <Link to="/groups/aor">
                  {aorText('Area of Responsibility')}
                </Link>{' '}
                is required.
              </li>
            )}
            {!hasTeams && (
              <li>
                At least one <Link to="/groups/teams">Team</Link> is required.
              </li>
            )}
            {hasCategoriesWithoutTeams && (
              <li>
                All <Link to="/groups/alert-categories">Alert Categories</Link>{' '}
                must be associated with at least one team.
              </li>
            )}
          </ul>
        </Alert>
      )}

      {/* Card 1: Main Config Details + Actions */}
      <AlertConfigSummary config={config} s={formatLastRun(config.lastRunAt)} />
      <div className="d-flex gap-2">
        {/* Edit Button */}
        <Link to={`/aialerts/${config.id}/edit`}>
          <StylishNewButton className="button--secondary button--sm">
            Edit
          </StylishNewButton>
        </Link>
        {/* Link to Reports */}
        <Link to={`/aialerts/${config.id}/reports`}>
          <StylishNewButton className="button--secondary button--sm">
            Reports
          </StylishNewButton>
        </Link>
        {/* Link to Logs */}
        <Link to={`/aialerts/${config.id}/logs`}>
          <StylishNewButton className="button--secondary button--sm">
            Logs
          </StylishNewButton>
        </Link>
        <StylishNewButton
          className="button--primary button--sm"
          onClick={handleTest}
          disabled={isTestRunning}
        >
          {isTestRunning ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-1"
              />
              Testing...
            </>
          ) : (
            'Test'
          )}
        </StylishNewButton>
        <StylishNewButton
          className="button--danger button--sm"
          onClick={() => setShowDeleteModal(true)}
          disabled={deleteConfig.isPending}
        >
          Delete
        </StylishNewButton>
      </div>

      {/* Activate Confirmation Modal */}
      <Modal
        show={showActivateModal}
        onHide={() => setShowActivateModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Test Run Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Before activating this alert configuration, you need to perform a test
          run to ensure it works correctly. Would you like to run a test now?
        </Modal.Body>
        <Modal.Footer>
          <StylishNewButton
            className="button--secondary"
            onClick={() => setShowActivateModal(false)}
          >
            Cancel
          </StylishNewButton>
          <StylishNewButton className="button--primary" onClick={handleTest}>
            Run Test
          </StylishNewButton>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this alert configuration? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <StylishNewButton
            className="button--secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </StylishNewButton>
          <StylishNewButton className="button--danger" onClick={handleDelete}>
            Delete
          </StylishNewButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AlertConfigSummaryPage;
